import React, { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';

import { Button, ContainerHeader, Toolbar } from '~components';
import Icon, { faKey, faPlus } from '~components/icon';
import { Row, TableView } from '~components/table-view';

import { CompanyAccessKey } from '~main/models';

import { getAll as getAllUsers } from '~care-workers/actions';
import { useGetCompanyAccessKeysQuery } from '~main/actions/companyAccessKeys';
import { setTitle } from '~main/actions/login';

import { selectUsers } from '~care-workers/selectors/users';

import AccessKeyModal from './components/AccessKeyModal';
import ConfirmDeleteModal from './components/ConfirmDeleteModal';
import columnsFactory from './forms/list';

const MAX_ACCESS_KEYS = 3;

const CompanyKeysContainer: React.FC = () => {
  useEffect(() => {
    setTitle('Access Tokens');
  }, []);

  const dispatch = useAppDispatch();
  const { data } = useGetCompanyAccessKeysQuery(undefined, { refetchOnMountOrArgChange: true });
  const users = useAppSelector(selectUsers);

  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    row: Partial<CompanyAccessKey> | undefined;
  }>({
    isOpen: false,
    row: undefined,
  });
  const [modalData, setModalData] = useState<{
    isOpen: boolean;
    isNew: boolean;
    rowData: CompanyAccessKey | undefined;
  }>({
    isOpen: false,
    isNew: false,
    rowData: undefined,
  });

  useEffect(() => {
    dispatch(getAllUsers());
  }, []);

  const reset = () => {
    setDeleteModal({ isOpen: false, row: undefined });
    setModalData({ isOpen: false, isNew: false, rowData: undefined });
  };

  const tableData = useMemo(
    () =>
      (data ?? []).map((token) => ({
        ...token,
        id: token.id?.toString(),
      })) as Row[],
    [data],
  );

  const columns = useMemo(
    () =>
      columnsFactory(users, (id: string) =>
        setModalData({ isOpen: true, isNew: false, rowData: data?.find((row) => String(row.id) === id) }),
      ),
    [users, data],
  );

  return (
    <div className="p-2">
      <ContainerHeader icon={faKey} iconSize="1x" className="info" dataCy="access-keys-header">
        Company Access Tokens
      </ContainerHeader>
      <Toolbar
        id="company-access-keys-toolbar"
        rightCluster={
          <>
            {data && data.length >= MAX_ACCESS_KEYS && (
              <div className="text-danger">Maximum of {MAX_ACCESS_KEYS} Tokens allowed.</div>
            )}
            <Button
              id="add-company-access-key-button"
              color="success"
              onClick={() => setModalData({ isOpen: true, isNew: true, rowData: undefined })}
              disabled={data && data.length >= MAX_ACCESS_KEYS}
              size="sm"
            >
              <Icon id="add-token-icon" icon={faPlus} className="me-1" />
              Add Token
            </Button>
          </>
        }
      />
      <TableView
        id="company-access-keys-tableview"
        data={tableData}
        columns={columns}
        striped
        emptyMessage="No access keys found. Please add one."
        emptyIcon={faPlus}
        // Delete-only actions: editonly suppresses the edit pencil while every column
        // is non-editable, so cells render read-only and only the delete action remains
        editonly
        showAddRow={false}
        onDeleteRow={(rowId) =>
          setDeleteModal({
            isOpen: true,
            row: tableData.find((row) => String(row.id) === String(rowId)) as Partial<CompanyAccessKey>,
          })
        }
      />
      <ConfirmDeleteModal isOpen={deleteModal.isOpen} reset={reset} row={deleteModal.row} />
      <AccessKeyModal
        isOpen={modalData.isOpen}
        isNew={modalData.isNew}
        rowData={modalData.rowData}
        toggle={reset}
        onRevoke={(row) => setDeleteModal({ isOpen: true, row })}
      />
    </div>
  );
};

export default CompanyKeysContainer;
